import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import ChecklistService, { ChecklistTemplate } from "@/app/_services/ChecklistService";
import { toast } from "@/app/_utils/toast";

import {
  allSectionKeys,
  DraftCategory,
  DraftField,
  DraftSection,
  fromSchema,
  move,
  moveTo,
  newCategory,
  newField,
  newSection,
  slugify,
  toSchema,
  uniqueKey,
} from "./checklistDraft";
import { Issue, issuesFromServer, validateDraft } from "./checklistValidation";
import { showValidationToast } from "./_cmp/ValidationToast";

type Details = { name: string; description: string };

// Sidebar selection for the fixed Details tab; any other value is a section uid.
export const DETAILS = "details";

export function useChecklistEditorModel() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const isNew = !templateId;

  const [saved, setSaved] = useState<ChecklistTemplate | null>(null);
  // Saved schema run through the same draft round trip, so key order and defaults match.
  const [savedSchemaJson, setSavedSchemaJson] = useState("");
  const [details, setDetails] = useState<Details>({ name: "", description: "" });
  const [categories, setCategories] = useState<DraftCategory[]>([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedUid, setSelectedUid] = useState<string>(DETAILS);
  // `nonce` re-triggers the scroll and flash when the same row is picked twice.
  const [highlight, setHighlight] = useState<{ uid: string; nonce: number } | null>(null);

  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;

  function reset(template: ChecklistTemplate) {
    setSaved(template);
    setDetails({ name: template.name, description: template.description });
    const next = fromSchema(template.schema);
    setCategories(next);
    setSavedSchemaJson(JSON.stringify(toSchema(next)));
    // Keep the open section across a save when its key survived, else fall back to Details.
    setSelectedUid((current) => {
      const currentKey = categoriesRef.current
        .flatMap((c) => c.sections)
        .find((s) => s.uid === current)?.key;
      return next.flatMap((c) => c.sections).find((s) => s.key === currentKey)?.uid ?? DETAILS;
    });
  }

  useEffect(() => {
    if (!templateId) return;
    setIsLoading(true);
    ChecklistService.getTemplateAsync(templateId)
      .then(reset)
      .catch((error) => {
        console.error(error);
        toast.error("Could not load this template");
      })
      .finally(() => setIsLoading(false));
  }, [templateId]);

  useEffect(() => {
    if (!highlight) return;
    const timer = setTimeout(() => setHighlight(null), 2000);
    return () => clearTimeout(timer);
  }, [highlight]);

  function focusIssue(issue: Issue) {
    const { target } = issue;
    const nonce = Date.now();
    if (target.kind === "details") {
      setSelectedUid(DETAILS);
      setHighlight({ uid: DETAILS, nonce });
    } else if (target.kind === "category") {
      setHighlight({ uid: target.categoryUid, nonce });
    } else {
      setSelectedUid(target.sectionUid);
      setHighlight({ uid: target.kind === "field" ? target.fieldUid : target.sectionUid, nonce });
    }
  }

  const highlightNonceFor = (uid: string) => (highlight?.uid === uid ? highlight.nonce : null);

  const selectedCategory =
    categories.find((c) => c.sections.some((s) => s.uid === selectedUid)) ?? null;
  const selectedSection = selectedCategory?.sections.find((s) => s.uid === selectedUid) ?? null;

  const schema = useMemo(() => toSchema(categories), [categories]);

  const isDirty = useMemo(() => {
    if (!saved) return Boolean(details.name || categories.length);
    return (
      details.name !== saved.name ||
      details.description !== saved.description ||
      JSON.stringify(schema) !== savedSchemaJson
    );
  }, [saved, savedSchemaJson, details, schema, categories.length]);

  const updateCategory = (uid: string, patch: (c: DraftCategory) => DraftCategory) =>
    setCategories((current) => current.map((c) => (c.uid === uid ? patch(c) : c)));

  const updateSection = (uid: string, patch: (s: DraftSection) => DraftSection) =>
    setCategories((current) =>
      current.map((c) =>
        c.sections.some((s) => s.uid === uid)
          ? { ...c, sections: c.sections.map((s) => (s.uid === uid ? patch(s) : s)) }
          : c
      )
    );

  function addCategory() {
    const category = newCategory(categories.map((c) => c.key));
    setCategories((current) => [...current, category]);
    return category.uid;
  }

  function renameCategory(uid: string, label: string) {
    setCategories((current) =>
      current.map((c) => {
        if (c.uid !== uid) return c;
        if (c.locked) return { ...c, label };
        const taken = current.filter((o) => o.uid !== uid).map((o) => o.key);
        return { ...c, label, key: uniqueKey(slugify(label, "category"), taken) };
      })
    );
  }

  function setCategoryIcon(uid: string, icon: string) {
    updateCategory(uid, (c) => ({ ...c, icon }));
  }

  function removeCategory(uid: string) {
    const removed = categories.find((c) => c.uid === uid);
    setCategories((current) => current.filter((c) => c.uid !== uid));
    if (removed?.sections.some((s) => s.uid === selectedUid)) setSelectedUid(DETAILS);
  }

  function reorderCategory(from: number, to: number) {
    setCategories((current) => moveTo(current, from, to));
  }

  /** Adds to the given category, else the open section's, else the last; creates one if none. */
  function addSection(categoryUid?: string) {
    const section = newSection(allSectionKeys(categories));
    let target =
      categories.find((c) => c.uid === categoryUid) ?? selectedCategory ?? categories.at(-1);
    let next = categories;
    if (!target) {
      target = { ...newCategory([]), label: "General", key: "general" };
      next = [target];
    }
    const targetUid = target.uid;
    setCategories(
      next.map((c) => (c.uid === targetUid ? { ...c, sections: [...c.sections, section] } : c))
    );
    setSelectedUid(section.uid);
    return section.uid;
  }

  function renameSection(uid: string, label: string) {
    setCategories((current) =>
      current.map((c) => ({
        ...c,
        sections: c.sections.map((s) => {
          if (s.uid !== uid) return s;
          if (s.locked) return { ...s, label };
          return { ...s, label, key: uniqueKey(slugify(label, "section"), allSectionKeys(current, uid)) };
        }),
      }))
    );
  }

  function removeSection(uid: string) {
    const category = categories.find((c) => c.sections.some((s) => s.uid === uid));
    if (!category) return;
    const index = category.sections.findIndex((s) => s.uid === uid);
    const remaining = category.sections.filter((s) => s.uid !== uid);
    updateCategory(category.uid, (c) => ({ ...c, sections: remaining }));
    if (selectedUid === uid) {
      setSelectedUid(remaining[Math.min(index, remaining.length - 1)]?.uid ?? DETAILS);
    }
  }

  /** Moves a section within or between categories; `toIndex` is the slot in the target list. */
  function moveSection(fromCategoryUid: string, fromIndex: number, toCategoryUid: string, toIndex: number) {
    setCategories((current) => {
      const source = current.find((c) => c.uid === fromCategoryUid);
      const section = source?.sections[fromIndex];
      if (!source || !section) return current;
      if (fromCategoryUid === toCategoryUid) {
        return current.map((c) =>
          c.uid === fromCategoryUid ? { ...c, sections: moveTo(c.sections, fromIndex, toIndex) } : c
        );
      }
      return current.map((c) => {
        if (c.uid === fromCategoryUid) return { ...c, sections: c.sections.filter((_, i) => i !== fromIndex) };
        if (c.uid !== toCategoryUid) return c;
        const sections = [...c.sections];
        sections.splice(Math.min(toIndex, sections.length), 0, section);
        return { ...c, sections };
      });
    });
  }

  function addField(sectionUid: string, atStart = false) {
    updateSection(sectionUid, (s) => {
      const field = newField(s.fields.map((f) => f.key));
      return { ...s, fields: atStart ? [field, ...s.fields] : [...s.fields, field] };
    });
  }

  function updateField(sectionUid: string, fieldUid: string, patch: Partial<DraftField>) {
    updateSection(sectionUid, (s) => ({
      ...s,
      fields: s.fields.map((f) => {
        if (f.uid !== fieldUid) return f;
        const next = { ...f, ...patch };
        if (patch.label !== undefined && !f.locked) {
          const taken = s.fields.filter((o) => o.uid !== fieldUid).map((o) => o.key);
          next.key = uniqueKey(slugify(patch.label, "field"), taken);
        }
        return next;
      }),
    }));
  }

  function removeField(sectionUid: string, fieldUid: string) {
    updateSection(sectionUid, (s) => ({ ...s, fields: s.fields.filter((f) => f.uid !== fieldUid) }));
  }

  function reorderField(sectionUid: string, from: number, to: number) {
    updateSection(sectionUid, (s) => ({ ...s, fields: moveTo(s.fields, from, to) }));
  }

  function moveField(sectionUid: string, index: number, delta: number) {
    updateSection(sectionUid, (s) => ({ ...s, fields: move(s.fields, index, delta) }));
  }

  async function save() {
    const issues = validateDraft(details.name, categories);
    if (issues.length) {
      showValidationToast(issues, focusIssue);
      return;
    }
    setIsSaving(true);
    const body = { ...details, schema };
    try {
      const result = templateId
        ? await ChecklistService.updateTemplateAsync(templateId, body)
        : await ChecklistService.createTemplateAsync(body);
      reset(result);
      toast.success("Template saved");
      if (isNew) navigate(`/admin/tools/checklists/${result.id}`, { replace: true });
    } catch (error) {
      const serverIssues = issuesFromServer(error, categories);
      if (serverIssues.length) showValidationToast(serverIssues, focusIssue);
      else toast.error("Could not save this template");
    } finally {
      setIsSaving(false);
    }
  }

  function discard() {
    if (saved) reset(saved);
    else navigate("/admin/tools/checklists");
  }

  return {
    isNew,
    isLoading,
    isSaving,
    isDirty,
    details,
    setDetails,
    categories,
    selectedUid,
    selectedCategory,
    selectedSection,
    setSelectedUid,
    addCategory,
    renameCategory,
    setCategoryIcon,
    removeCategory,
    reorderCategory,
    addSection,
    renameSection,
    removeSection,
    moveSection,
    addField,
    updateField,
    removeField,
    moveField,
    reorderField,
    highlightNonceFor,
    save,
    discard,
  };
}
